import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Folder, 
  File, 
  ChevronRight, 
  ChevronDown, 
  Upload, 
  Archive, 
  Search,
  Filter,
  SortAsc,
  SortDesc,
  CheckSquare,
  Square,
  Loader2,
  AlertCircle,
  Calendar,
  HardDrive
} from 'lucide-react';
import { apiService } from '../services/api';
import { FileMetadata, FolderStructure } from '../../../shared/types';
import { formatFileSize, formatDate } from '../utils/formatters';

interface FileBrowserProps {
  onFileSelect?: (files: FileMetadata[]) => void;
  onFolderSelect?: (folders: FolderStructure[]) => void;
  className?: string;
}

type SortField = 'name' | 'size' | 'uploadDate' | 'storageTier';
type SortOrder = 'asc' | 'desc';
type StorageTier = 'upload' | 'archive' | 'all';

interface ExpandedFolders {
  [folderPath: string]: boolean;
}

export const FileBrowser: React.FC<FileBrowserProps> = ({
  onFileSelect,
  onFolderSelect,
  className = ''
}) => {
  // State management
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [selectedFolders, setSelectedFolders] = useState<Set<string>>(new Set());
  const [expandedFolders, setExpandedFolders] = useState<ExpandedFolders>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [tierFilter, setTierFilter] = useState<StorageTier>('all');
  const [folderFilter, setFolderFilter] = useState<string>('');

  // Data fetching
  const { 
    data: foldersData, 
    isLoading: foldersLoading, 
    error: foldersError,
    refetch: refetchFolders
  } = useQuery({
    queryKey: ['folders'],
    queryFn: () => apiService.listFolders(),
  });

  const { 
    data: filesData, 
    isLoading: filesLoading, 
    error: filesError,
    refetch: refetchFiles
  } = useQuery({
    queryKey: ['files', tierFilter === 'all' ? undefined : tierFilter, folderFilter || undefined],
    queryFn: () => apiService.listFiles({
      storageTier: tierFilter === 'all' ? undefined : tierFilter,
      folderPath: folderFilter || undefined
    }),
  });

  const folders = foldersData?.folders || [];
  const files = filesData?.files || [];

  // Filtered and sorted data
  const filteredAndSortedFiles = useMemo(() => {
    let filtered = files;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(file => 
        file.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        file.folderPath.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort files
    return filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = a.fileName.toLowerCase();
          bValue = b.fileName.toLowerCase();
          break;
        case 'size':
          aValue = a.sizeBytes;
          bValue = b.sizeBytes;
          break;
        case 'uploadDate':
          aValue = new Date(a.uploadDate);
          bValue = new Date(b.uploadDate);
          break;
        case 'storageTier':
          aValue = a.storageTier;
          bValue = b.storageTier;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [files, searchTerm, sortField, sortOrder]);

  const filteredAndSortedFolders = useMemo(() => {
    let filtered = folders;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(folder => 
        folder.concertName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        folder.folderPath.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort folders by name
    return filtered.sort((a, b) => {
      const aName = a.concertName.toLowerCase();
      const bName = b.concertName.toLowerCase();
      return sortOrder === 'asc' ? aName.localeCompare(bName) : bName.localeCompare(aName);
    });
  }, [folders, searchTerm, sortOrder]);

  // Group files by folder
  const filesByFolder = useMemo(() => {
    const grouped: { [folderPath: string]: FileMetadata[] } = {};
    filteredAndSortedFiles.forEach(file => {
      if (!grouped[file.folderPath]) {
        grouped[file.folderPath] = [];
      }
      grouped[file.folderPath].push(file);
    });
    return grouped;
  }, [filteredAndSortedFiles]);

  // Event handlers
  const handleFileSelect = (file: FileMetadata, isSelected: boolean) => {
    const newSelected = new Set(selectedFiles);
    if (isSelected) {
      newSelected.add(file.fileId);
    } else {
      newSelected.delete(file.fileId);
    }
    setSelectedFiles(newSelected);
    
    if (onFileSelect) {
      const selectedFileObjects = filteredAndSortedFiles.filter(f => newSelected.has(f.fileId));
      onFileSelect(selectedFileObjects);
    }
  };

  const handleFolderSelect = (folder: FolderStructure, isSelected: boolean) => {
    const newSelected = new Set(selectedFolders);
    if (isSelected) {
      newSelected.add(folder.folderPath);
    } else {
      newSelected.delete(folder.folderPath);
    }
    setSelectedFolders(newSelected);
    
    if (onFolderSelect) {
      const selectedFolderObjects = filteredAndSortedFolders.filter(f => newSelected.has(f.folderPath));
      onFolderSelect(selectedFolderObjects);
    }
  };

  const handleSelectAllFiles = () => {
    if (selectedFiles.size === filteredAndSortedFiles.length) {
      setSelectedFiles(new Set());
      onFileSelect?.([]);
    } else {
      const allFileIds = new Set(filteredAndSortedFiles.map(f => f.fileId));
      setSelectedFiles(allFileIds);
      onFileSelect?.(filteredAndSortedFiles);
    }
  };

  const toggleFolderExpansion = (folderPath: string) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderPath]: !prev[folderPath]
    }));
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />;
  };

  const isLoading = foldersLoading || filesLoading;
  const hasError = foldersError || filesError;

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      {/* Header with controls */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col space-y-4">
          {/* Search and filters row */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search files and folders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Storage tier filter */}
            <div className="flex items-center space-x-2">
              <Filter className="text-gray-400 w-4 h-4" />
              <select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value as StorageTier)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Tiers</option>
                <option value="upload">Upload Tier</option>
                <option value="archive">Archive Tier</option>
              </select>
            </div>
          </div>

          {/* Selection info and actions */}
          {(selectedFiles.size > 0 || selectedFolders.size > 0) && (
            <div className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-md">
              <span className="text-sm text-blue-700">
                {selectedFiles.size} files and {selectedFolders.size} folders selected
              </span>
              <button
                onClick={() => {
                  setSelectedFiles(new Set());
                  setSelectedFolders(new Set());
                  onFileSelect?.([]);
                  onFolderSelect?.([]);
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear selection
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-600">Loading files and folders...</span>
          </div>
        ) : hasError ? (
          <div className="flex items-center justify-center py-12">
            <AlertCircle className="w-6 h-6 text-red-400" />
            <span className="ml-2 text-red-600">Error loading data. Please try again.</span>
          </div>
        ) : (
          <div className="h-96 overflow-y-auto">
            {/* Dual pane layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
              {/* Upload Tier Pane */}
              <div className="p-4">
                <div className="flex items-center space-x-2 mb-4">
                  <Upload className="w-5 h-5 text-green-600" />
                  <h3 className="font-medium text-gray-900">Upload Tier</h3>
                  <span className="text-sm text-gray-500">
                    ({folders.filter(f => f.folderPath.startsWith('/upload')).length} folders)
                  </span>
                </div>
                
                <FolderTree
                  folders={filteredAndSortedFolders.filter(f => f.folderPath.startsWith('/upload'))}
                  files={filesByFolder}
                  expandedFolders={expandedFolders}
                  selectedFiles={selectedFiles}
                  selectedFolders={selectedFolders}
                  onToggleFolder={toggleFolderExpansion}
                  onFileSelect={handleFileSelect}
                  onFolderSelect={handleFolderSelect}
                  sortField={sortField}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                  getSortIcon={getSortIcon}
                />
              </div>

              {/* Archive Tier Pane */}
              <div className="p-4">
                <div className="flex items-center space-x-2 mb-4">
                  <Archive className="w-5 h-5 text-blue-600" />
                  <h3 className="font-medium text-gray-900">Archive Tier</h3>
                  <span className="text-sm text-gray-500">
                    ({folders.filter(f => f.folderPath.startsWith('/archive')).length} folders)
                  </span>
                </div>
                
                <FolderTree
                  folders={filteredAndSortedFolders.filter(f => f.folderPath.startsWith('/archive'))}
                  files={filesByFolder}
                  expandedFolders={expandedFolders}
                  selectedFiles={selectedFiles}
                  selectedFolders={selectedFolders}
                  onToggleFolder={toggleFolderExpansion}
                  onFileSelect={handleFileSelect}
                  onFolderSelect={handleFolderSelect}
                  sortField={sortField}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                  getSortIcon={getSortIcon}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer with file count and selection info */}
      <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            {filteredAndSortedFiles.length} files in {filteredAndSortedFolders.length} folders
          </span>
          {selectedFiles.size > 0 && (
            <button
              onClick={handleSelectAllFiles}
              className="flex items-center space-x-1 text-blue-600 hover:text-blue-800"
            >
              {selectedFiles.size === filteredAndSortedFiles.length ? (
                <CheckSquare className="w-4 h-4" />
              ) : (
                <Square className="w-4 h-4" />
              )}
              <span>
                {selectedFiles.size === filteredAndSortedFiles.length ? 'Deselect all' : 'Select all'}
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Folder Tree Component
interface FolderTreeProps {
  folders: FolderStructure[];
  files: { [folderPath: string]: FileMetadata[] };
  expandedFolders: ExpandedFolders;
  selectedFiles: Set<string>;
  selectedFolders: Set<string>;
  onToggleFolder: (folderPath: string) => void;
  onFileSelect: (file: FileMetadata, isSelected: boolean) => void;
  onFolderSelect: (folder: FolderStructure, isSelected: boolean) => void;
  sortField: SortField;
  sortOrder: SortOrder;
  onSort: (field: SortField) => void;
  getSortIcon: (field: SortField) => React.ReactNode;
}

const FolderTree: React.FC<FolderTreeProps> = ({
  folders,
  files,
  expandedFolders,
  selectedFiles,
  selectedFolders,
  onToggleFolder,
  onFileSelect,
  onFolderSelect,
  sortField,
  sortOrder,
  onSort,
  getSortIcon
}) => {
  if (folders.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Folder className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        <p>No folders found</p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {folders.map((folder) => {
        const isExpanded = expandedFolders[folder.folderPath];
        const folderFiles = files[folder.folderPath] || [];
        const isSelected = selectedFolders.has(folder.folderPath);

        return (
          <div key={folder.folderPath} className="border border-gray-200 rounded-md">
            {/* Folder header */}
            <div
              className={`flex items-center space-x-2 p-3 cursor-pointer hover:bg-gray-50 ${
                isSelected ? 'bg-blue-50 border-blue-200' : ''
              }`}
              onClick={() => onToggleFolder(folder.folderPath)}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onFolderSelect(folder, !isSelected);
                }}
                className="flex-shrink-0"
              >
                {isSelected ? (
                  <CheckSquare className="w-4 h-4 text-blue-600" />
                ) : (
                  <Square className="w-4 h-4 text-gray-400" />
                )}
              </button>
              
              {folderFiles.length > 0 ? (
                isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                )
              ) : (
                <div className="w-4 h-4" />
              )}
              
              <Folder className="w-4 h-4 text-gray-600" />
              
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {folder.concertName}
                </p>
                <p className="text-xs text-gray-500">
                  {folder.fileCount} files • {formatFileSize(folder.totalSizeBytes)}
                </p>
              </div>
              
              <div className="text-xs text-gray-500">
                {formatDate(folder.createdDate)}
              </div>
            </div>

            {/* Files list */}
            {isExpanded && folderFiles.length > 0 && (
              <div className="border-t border-gray-200 bg-gray-50">
                {/* File list header with sorting */}
                <div className="px-3 py-2 border-b border-gray-200 bg-white">
                  <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500">
                    <div className="col-span-1"></div> {/* Checkbox column */}
                    <button
                      onClick={() => onSort('name')}
                      className="col-span-5 flex items-center space-x-1 hover:text-gray-700"
                    >
                      <span>Name</span>
                      {getSortIcon('name')}
                    </button>
                    <button
                      onClick={() => onSort('size')}
                      className="col-span-2 flex items-center space-x-1 hover:text-gray-700"
                    >
                      <span>Size</span>
                      {getSortIcon('size')}
                    </button>
                    <button
                      onClick={() => onSort('uploadDate')}
                      className="col-span-3 flex items-center space-x-1 hover:text-gray-700"
                    >
                      <span>Date</span>
                      {getSortIcon('uploadDate')}
                    </button>
                    <button
                      onClick={() => onSort('storageTier')}
                      className="col-span-1 flex items-center space-x-1 hover:text-gray-700"
                    >
                      <HardDrive className="w-3 h-3" />
                      {getSortIcon('storageTier')}
                    </button>
                  </div>
                </div>

                {/* File rows */}
                <div className="max-h-64 overflow-y-auto">
                  {folderFiles.map((file) => {
                    const isFileSelected = selectedFiles.has(file.fileId);
                    
                    return (
                      <div
                        key={file.fileId}
                        className={`grid grid-cols-12 gap-2 px-3 py-2 hover:bg-gray-100 ${
                          isFileSelected ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="col-span-1 flex items-center">
                          <button
                            onClick={() => onFileSelect(file, !isFileSelected)}
                            className="flex-shrink-0"
                          >
                            {isFileSelected ? (
                              <CheckSquare className="w-4 h-4 text-blue-600" />
                            ) : (
                              <Square className="w-4 h-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                        
                        <div className="col-span-5 flex items-center space-x-2 min-w-0">
                          <File className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm text-gray-900 truncate" title={file.fileName}>
                            {file.fileName}
                          </span>
                        </div>
                        
                        <div className="col-span-2 flex items-center">
                          <span className="text-sm text-gray-600">
                            {formatFileSize(file.sizeBytes)}
                          </span>
                        </div>
                        
                        <div className="col-span-3 flex items-center">
                          <span className="text-sm text-gray-600">
                            {formatDate(file.uploadDate)}
                          </span>
                        </div>
                        
                        <div className="col-span-1 flex items-center">
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                              file.storageTier === 'upload'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {file.storageTier === 'upload' ? 'U' : 'A'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};